import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { LoginPage } from '@/features/auth/LoginPage';
import { SetPasswordPage } from '@/features/auth/SetPasswordPage';
import { ItemsPage } from '@/features/items/ItemsPage';
import { ActivityPage } from '@/features/project/ActivityPage';
import { MembersPage } from '@/features/project/MembersPage';
import { OverviewPage } from '@/features/project/OverviewPage';
import { AssetsPage } from '@/features/assets/AssetsPage';
import { SettingsPage } from '@/features/project/SettingsPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { ProjectsPage } from '@/features/projects/ProjectsPage';
import { FinancePage } from '@/features/resources/FinancePage';
import { ResourcesPage } from '@/features/resources/ResourcesPage';
import { StagesPage } from '@/features/stages/StagesPage';
import { SystemStatusPage } from '@/features/system/SystemStatusPage';
import { AppLayout, ProjectLayout, RequireAuth } from './guards';
import { queryClient } from './query-client';
import { DisplaySettingsProvider } from './display-settings';
import { ThemeProvider } from './theme';

/**
 * Маршруты приложения.
 *
 * Соответствуют карте страниц спецификации (документ «Итоговое описание», §22).
 * Заглушек больше нет: каждый пункт бокового меню ведёт на работающий раздел.
 */
export function App() {
  return (
    <ThemeProvider>
      <DisplaySettingsProvider>
        <QueryClientProvider client={queryClient}>
          {/* Подсказки нужны во всех разделах, поэтому провайдер один на приложение. */}
          <TooltipProvider delayDuration={400}>
            <BrowserRouter>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/set-password" element={<SetPasswordPage />} />

                <Route element={<RequireAuth />}>
                  <Route element={<AppLayout />}>
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/system" element={<SystemStatusPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                  </Route>

                  <Route path="/projects/:projectId" element={<ProjectLayout />}>
                    <Route index element={<OverviewPage />} />
                    <Route path="items" element={<ItemsPage />} />
                    <Route path="stages" element={<StagesPage />} />
                    <Route path="assets" element={<AssetsPage />} />
                    <Route path="resources" element={<ResourcesPage />} />
                    <Route path="finance" element={<FinancePage />} />
                    <Route path="activity" element={<ActivityPage />} />
                    <Route path="members" element={<MembersPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                  </Route>
                </Route>

                <Route path="*" element={<Navigate to="/projects" replace />} />
              </Routes>
            </BrowserRouter>
          </TooltipProvider>
          <Toaster />
        </QueryClientProvider>
      </DisplaySettingsProvider>
    </ThemeProvider>
  );
}
